import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import {
  getQuestionSchemaRogerdocQuestionSchemaGet,
  getVisitSchemaRogerdocV2VisitsVisitIdSchemaGet,
  listEnabledSectionsBespokeTemplatesEnabledGet,
  validateOasisVisitsVisitIdValidateOasisPost,
} from '../../services/api/sdk.gen';
import { VisitTypeId } from '../../services/api/models/VisitTypeId';
import {
  OASISSchema,
  OASISFormData,
  Question,
  FormSchemaVersion,
} from './types';
import type {
  ContradictionFieldRef,
  ContradictionFinding,
  EnabledBespokeEntry,
  OasisValidationError,
  RogerDocFormType,
} from '../../services/api/types.gen';
import { OASISQuestionRenderer } from './OASISQuestionRenderer';
import { OASISNavigation } from './OASISNavigation';
import { VisitActiveDiagnosis } from './VisitActiveDiagnosis';
import { CmsIssueCard } from './CmsIssueCard';
import { ContradictionFindingCard } from './contradictions/ContradictionFindingCard';
import {
  contradictionLocationIsOnCategory,
  contradictionLocationNeedsActiveDiagnosisCategory,
  findContradictionSourceTarget,
  pickContradictionJumpLocation,
} from './contradictions/contradictionNavigation';
import {
  buildAdvisoryScrubberItems,
  buildFatalScrubberErrors,
  countCmsWarningsByCategory,
  countFatalsByCategory,
  countQaFindingsByCategory,
  SCRUBBER_TAB_DEFINITIONS,
  SCRUBBER_TABS,
} from './contradictions/scrubberAdvisory';
import type {
  ScrubberListItem,
  ScrubberTab,
} from './contradictions/scrubberAdvisory';
import {
  escapeCssAttributeValue,
  isVisibleOasisNavigationTarget,
} from './oasisNavigationVisibility';
import {
  resolveBespokeSection,
  type SchemaQuestionOption,
  type BespokeDbConfig,
} from './bespoke/bespokeRegistry';
import { pinFromFormSchemaVersion } from '../../pages/formSchema/formSchemaVersionPin';
import { useOASISFormStore } from '../../store/slices/oasisFormSlice';
import { useDebounce } from '../../hooks/useDebounce';
import { getUserFacingApiMessage } from '../../utils/apiErrorDetail';
import { getOASISFormData, getSubitemClearUpdates } from './utils';
import {
  resolveQuestionSpan,
  getOasisAutosaveDebounceMs,
  GridSpan,
  SPAN_CLASSES,
} from './OASISFormUtils';
import { Loader2, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { SaveDeclinedError, useSaveStatus } from '../../hooks/useAutoSave';
import type { AutoSaveStatus } from '../../hooks/useAutoSave';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import { FeatureFlag } from '../../constants/featureFlags';
import { NON_OASIS_WHOLE_VISIT_SECTION_NAME } from '../../constants/nonOasisFormSchema';
import {
  showsGoalsSection,
  isSNDiscipline,
  supportsActiveDiagnosisPage,
  supportsWounds,
} from '../../constants/visitTypes';
import {
  ACTIVE_DIAGNOSIS_CATEGORY,
  GOALS_CATEGORY,
  buildLabelByCategory,
  resolveCategoryLabel,
} from './OASISFormLabels';
import { PatientWounds } from '../wounds';
import { PatientMedications } from '../medications';
import PatientAllergies from '../PatientAllergies';
import GoalsTab, { type GoalsTabVisit } from '../goals/GoalsTab';
import type {
  OrderResponse,
  PatientResponse,
  TaskStatus,
} from '../../services/api/types.gen';

/**
 * Canonical OASIS-E2 section ids (the `section_id`s Roger-Docs-API emits) whose
 * sections host an embedded module. Keyed off the section id directly rather than
 * matching on the display label. Ideally these would live in a shared enum of all
 * section names.
 */
const SKIN_SECTION_ID = 'Skin_Conditions';
const MEDICATIONS_SECTION_ID = 'Medications';

/** Stable no-op so the embedded GoalsTab's `onClose` prop keeps a stable identity. */
const noop = () => {};

interface OASISFormProps {
  visitId: string;
  visitType: VisitTypeId;
  /** From API `VisitResponse.is_oasis` / `VISIT_TYPE_INFO.is_oasis` — never infer from `visitType` string shape. */
  isOasis: boolean;
  initialData?: OASISFormData;
  onSave: (data: OASISFormData) => void | Promise<void>;
  hasActiveSignatures?: boolean;
  showInlineValidation?: boolean;
  /**
   * Reports autosave status changes so the parent can render an indicator.
   * changeToken increments on every edit that resets the debounce timer.
   */
  onSaveStatusChange?: (status: AutoSaveStatus, changeToken: number) => void;
  /** Patient the visit belongs to; used by the embedded wounds and meds modules. */
  patientId: string;
  /**
   * Full patient record; used by the embedded allergies module. Optional so the
   * form still renders while the parent is loading it; the allergies section is
   * withheld until it is available.
   */
  patient?: PatientResponse | null;
  /** Current visit status; gates editing in the embedded modules. */
  visitStatus: TaskStatus;
  /** Episode the visit belongs to; scopes the embedded meds module's orders. */
  episodeId?: string;
  /** Episode window; used by the embedded goals section. */
  episodeStartDate?: string | null;
  episodeEndDate?: string | null;
  /** Forwarded to the embedded meds module when a medication order is created. */
  onMedicationOrderCreated?: (order: OrderResponse) => void;
}

const OASIS_SCRUBBER_HIGHLIGHT_CLASSES = [
  'ring-2',
  'ring-primary',
  'ring-offset-2',
  'rounded-md',
  'transition',
];
const OASIS_SCRUBBER_HIGHLIGHT_MS = 3000;

interface ScrubberNavigationTarget {
  qid: string | null;
  element: HTMLElement;
}

interface PendingScrubberNavigation {
  qids: string[];
  attemptedCategories: string[];
}

interface PendingContradictionNavigation {
  location: ContradictionFieldRef;
}

const findVisibleOasisNavigationTarget = (
  qids: string[]
): ScrubberNavigationTarget | null => {
  for (const qid of qids) {
    const classicTarget = document.getElementById(`oasis-q-${qid}`);
    if (
      classicTarget instanceof HTMLElement &&
      isVisibleOasisNavigationTarget(classicTarget)
    ) {
      return { qid, element: classicTarget };
    }

    const safeQid = escapeCssAttributeValue(qid);
    const bespokeTargets = document.querySelectorAll<HTMLElement>(
      `[data-qid="${safeQid}"]`
    );
    for (const bespokeTarget of bespokeTargets) {
      if (isVisibleOasisNavigationTarget(bespokeTarget)) {
        return { qid, element: bespokeTarget };
      }
    }
  }

  return null;
};

const getNextUnattemptedCategory = (
  qids: string[],
  qidToCategoryMap: Record<string, string>,
  attemptedCategories: string[]
) => {
  const attempted = new Set(attemptedCategories);
  for (const qid of qids) {
    const category = qidToCategoryMap[qid];
    if (category && !attempted.has(category)) return category;
  }
  return null;
};

const highlightOasisNavigationTarget = (element: HTMLElement) => {
  element.classList.add(...OASIS_SCRUBBER_HIGHLIGHT_CLASSES);
  window.setTimeout(() => {
    element.classList.remove(...OASIS_SCRUBBER_HIGHLIGHT_CLASSES);
  }, OASIS_SCRUBBER_HIGHLIGHT_MS);
};

/** A flattened grid item - either a standalone question or context about its parent */
interface GridItem {
  question: Question;
  span: GridSpan;
}

const getDirectSubitemCount = (question: Question): number =>
  Object.values(question.subitems ?? {}).reduce(
    (sum, subitems) => sum + subitems.length,
    0
  );

/**
 * Resolve container span for subgroup parents:
 * - schema width wins
 * - otherwise use a compact default based on how many direct subitems exist
 */
const resolveSubgroupContainerSpan = (question: Question): GridSpan => {
  if (
    question.roger_emr_display_width !== undefined &&
    question.roger_emr_display_width >= 1 &&
    question.roger_emr_display_width <= 12
  ) {
    return question.roger_emr_display_width as GridSpan;
  }

  const directSubitems = getDirectSubitemCount(question);
  if (directSubitems <= 1) return 3;
  if (directSubitems <= 2) return 6;
  if (directSubitems <= 4) return 9;
  return 12;
};

/**
 * Maps each question to a grid item, sizing its span by whether it has subitems.
 */
const flattenQuestions = (questions: Question[]): GridItem[] => {
  return questions.map(question => {
    if (question.subitems && Object.keys(question.subitems).length > 0) {
      return { question, span: resolveSubgroupContainerSpan(question) };
    }
    return { question, span: resolveQuestionSpan(question) };
  });
};

const buildQidToCategoryMap = (schema: OASISSchema | null) => {
  const map: Record<string, string> = {};
  if (!schema) return map;

  const collectQuestion = (question: Question, category: string) => {
    if (question.qid) {
      map[question.qid] = category;
    }
    if (!question.subitems) return;
    Object.values(question.subitems).forEach(subitems => {
      subitems.forEach(subitem => collectQuestion(subitem, category));
    });
  };

  Object.entries(schema).forEach(([category, questions]) => {
    questions.forEach(question => collectQuestion(question, category));
  });

  return map;
};

const buildQidToQuestionText = (schema: OASISSchema | null) => {
  const map: Record<string, string> = {};
  if (!schema) return map;

  const collect = (question: Question) => {
    if (question.qid && question.question) {
      map[question.qid] = question.question;
    }
    if (!question.subitems) return;
    Object.values(question.subitems).forEach(subitems => {
      subitems.forEach(sub => collect(sub));
    });
  };

  Object.values(schema).forEach(questions => {
    questions.forEach(q => collect(q));
  });

  return map;
};

const OASISFormComponent: React.FC<OASISFormProps> = ({
  visitId,
  visitType,
  isOasis,
  initialData,
  onSave,
  hasActiveSignatures = false,
  showInlineValidation = false,
  onSaveStatusChange,
  patientId,
  patient,
  visitStatus,
  episodeId,
  episodeStartDate,
  episodeEndDate,
  onMedicationOrderCreated,
}) => {
  const {
    schema,
    formData,
    currentCategory,
    isLoading,
    error,
    schemaVersion,
    setSchema,
    setFormData,
    setCurrentCategory,
    setIsLoading,
    setError,
    setLastSaved,
    setSchemaVersion,
  } = useOASISFormStore();

  const { hasFeatureFlag } = useStore(
    useShallow(state => ({
      hasFeatureFlag: state.hasFeatureFlag,
    }))
  );
  const useVersionedFormSchemas = hasFeatureFlag(
    FeatureFlag.VERSIONED_FORM_SCHEMAS
  );
  // QA findings only exist for flagged agencies, so everyone else would get a
  // permanently empty QA tab (EMR-809 review).
  const qaTabEnabled = hasFeatureFlag(FeatureFlag.CONTRADICTION_RULES_ENABLED);

  const [categories, setCategories] = useState<string[]>([]);
  // `section_id → display_name` from the active FSV's section_order. Empty
  // map for FSVs that don't ship one yet, in which case `resolveCategoryLabel`
  // falls back to the legacy regex transform.
  const labelByCategory = useMemo(
    () => buildLabelByCategory(schemaVersion),
    [schemaVersion]
  );
  /** Default (null) durations latch outcomes until the next edit; the pill renders them directly. */
  const { saveStatus, changeToken, reportEdit, beginSave, resolveSave } =
    useSaveStatus();
  /** Per-form DB bespoke template map (schema version → section → entry). */
  const [bespokeDbConfig, setBespokeDbConfig] = useState<BespokeDbConfig>(
    () => new Map()
  );
  const [inlineValidationErrors, setInlineValidationErrors] = useState<
    OasisValidationError[]
  >([]);
  const [inlineContradictions, setInlineContradictions] = useState<
    ContradictionFinding[]
  >([]);
  const [inlineValidationLoading, setInlineValidationLoading] = useState(false);
  const [inlineValidationErrorMessage, setInlineValidationErrorMessage] =
    useState<string | null>(null);
  const [hasValidatedOnce, setHasValidatedOnce] = useState(false);
  const [isScrubberExpanded, setIsScrubberExpanded] = useState(false);
  // Mobile-only collapsible section picker (replaces the desktop side nav)
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  // Close the picker whenever the section changes, including navigation
  // driven outside the picker (e.g. Live Scrubber jump-to-question).
  useEffect(() => {
    setIsMobileNavOpen(false);
  }, [currentCategory]);
  // Shared across sections by design — switching section keeps the chosen tab.
  const [scrubberTab, setScrubberTab] = useState<ScrubberTab>('section');
  const [highlightedQid, setHighlightedQid] = useState<string | null>(null);
  const [pendingScrubberNavigation, setPendingScrubberNavigation] =
    useState<PendingScrubberNavigation | null>(null);
  const [pendingContradictionNavigation, setPendingContradictionNavigation] =
    useState<PendingContradictionNavigation | null>(null);
  const hasRunInitialValidation = useRef(false);

  /** Avoid resetting local edits when parent `initialData` updates after autosave. */
  const hydratedVisitIdRef = useRef<string | null>(null);

  const qidToCategoryMap = useMemo(
    () => buildQidToCategoryMap(schema),
    [schema]
  );
  const qidToQuestionText = useMemo(
    () => buildQidToQuestionText(schema),
    [schema]
  );
  // Sidebar dots follow the active tab: the CMS tabs and the QA tab are read by
  // different people, so each marks only its own audience's sections (EMR-809).
  const fatalCountByCategory = useMemo(
    () =>
      scrubberTab === 'qa'
        ? {}
        : countFatalsByCategory(inlineValidationErrors, qidToCategoryMap),
    [scrubberTab, inlineValidationErrors, qidToCategoryMap]
  );
  const advisoryCountByCategory = useMemo(
    () =>
      scrubberTab === 'qa'
        ? countQaFindingsByCategory(inlineContradictions, qidToCategoryMap)
        : countCmsWarningsByCategory(inlineValidationErrors, qidToCategoryMap),
    [
      scrubberTab,
      inlineValidationErrors,
      inlineContradictions,
      qidToCategoryMap,
    ]
  );

  const navigateToResolvedTarget = useCallback(
    ({ qid, element }: ScrubberNavigationTarget) => {
      setHighlightedQid(qid);
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      window.setTimeout(() => highlightOasisNavigationTarget(element), 0);
      window.setTimeout(
        () => setHighlightedQid(null),
        OASIS_SCRUBBER_HIGHLIGHT_MS
      );
    },
    []
  );

  const navigateToQids = useCallback(
    (qids: string[]) => {
      const uniqueQids = Array.from(new Set(qids.filter(Boolean)));
      if (uniqueQids.length === 0) return false;

      const target = findVisibleOasisNavigationTarget(uniqueQids);
      if (target) {
        setPendingScrubberNavigation(null);
        navigateToResolvedTarget(target);
        return true;
      }

      const attemptedCategories = currentCategory ? [currentCategory] : [];
      const nextCategory = getNextUnattemptedCategory(
        uniqueQids,
        qidToCategoryMap,
        attemptedCategories
      );

      if (!nextCategory) {
        setPendingScrubberNavigation(null);
        return false;
      }

      setPendingScrubberNavigation({
        qids: uniqueQids,
        attemptedCategories: [...attemptedCategories, nextCategory],
      });
      setCurrentCategory(nextCategory);
      return true;
    },
    [
      qidToCategoryMap,
      currentCategory,
      setCurrentCategory,
      navigateToResolvedTarget,
    ]
  );

  const navigateToError = useCallback(
    (err: OasisValidationError) => {
      navigateToQids(err.qids || []);
    },
    [navigateToQids]
  );

  const navigateToContradictionLocation = useCallback(
    (location: ContradictionFieldRef) => {
      if (location.qids?.length && navigateToQids(location.qids)) {
        return;
      }

      const sourceTarget = findContradictionSourceTarget(location);
      if (sourceTarget) {
        setPendingContradictionNavigation(null);
        navigateToResolvedTarget({ qid: null, element: sourceTarget });
        return;
      }

      if (
        contradictionLocationNeedsActiveDiagnosisCategory(location) &&
        currentCategory !== ACTIVE_DIAGNOSIS_CATEGORY
      ) {
        setPendingContradictionNavigation({ location });
        setCurrentCategory(ACTIVE_DIAGNOSIS_CATEGORY);
      }
    },
    [
      navigateToQids,
      navigateToResolvedTarget,
      currentCategory,
      setCurrentCategory,
    ]
  );

  const navigateToContradiction = useCallback(
    (finding: ContradictionFinding) => {
      const localLocations = (finding.error_locations || []).filter(location =>
        contradictionLocationIsOnCategory(
          location,
          currentCategory,
          qidToCategoryMap
        )
      );

      if (localLocations.length > 0) {
        const resolvedTargets: ScrubberNavigationTarget[] = [];
        for (const location of localLocations) {
          if (location.qids?.length) {
            const target = findVisibleOasisNavigationTarget(location.qids);
            if (target) resolvedTargets.push(target);
            continue;
          }
          const element = findContradictionSourceTarget(location);
          if (element) {
            resolvedTargets.push({ qid: null, element });
          }
        }

        if (resolvedTargets.length > 0) {
          setPendingContradictionNavigation(null);
          setPendingScrubberNavigation(null);
          // Highlight every on-page location; scroll to the first (rule order).
          const scrollTarget = resolvedTargets[0];
          setHighlightedQid(scrollTarget.qid);
          scrollTarget.element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
          window.setTimeout(() => {
            for (const target of resolvedTargets) {
              highlightOasisNavigationTarget(target.element);
            }
          }, 0);
          window.setTimeout(
            () => setHighlightedQid(null),
            OASIS_SCRUBBER_HIGHLIGHT_MS
          );
          return;
        }

        navigateToContradictionLocation(localLocations[0]);
        return;
      }

      // Nothing on this page — first error_locations entry (rule JSON order).
      const location = pickContradictionJumpLocation(
        finding,
        currentCategory,
        qidToCategoryMap
      );
      if (location) navigateToContradictionLocation(location);
    },
    [navigateToContradictionLocation, currentCategory, qidToCategoryMap]
  );

  useLayoutEffect(() => {
    if (!pendingScrubberNavigation) return;

    const target = findVisibleOasisNavigationTarget(
      pendingScrubberNavigation.qids
    );
    if (target) {
      navigateToResolvedTarget(target);
      setPendingScrubberNavigation(null);
      return;
    }

    const nextCategory = getNextUnattemptedCategory(
      pendingScrubberNavigation.qids,
      qidToCategoryMap,
      pendingScrubberNavigation.attemptedCategories
    );
    if (!nextCategory) {
      setPendingScrubberNavigation(null);
      return;
    }

    setPendingScrubberNavigation({
      qids: pendingScrubberNavigation.qids,
      attemptedCategories: [
        ...pendingScrubberNavigation.attemptedCategories,
        nextCategory,
      ],
    });
    setCurrentCategory(nextCategory);
  }, [
    pendingScrubberNavigation,
    qidToCategoryMap,
    setCurrentCategory,
    navigateToResolvedTarget,
  ]);

  useEffect(() => {
    if (!pendingContradictionNavigation) return;
    const { location } = pendingContradictionNavigation;

    // Active Diagnosis mounts async (spinner, then anchors). Retry until the
    // contradiction source node appears or we time out.
    const tryResolve = () => {
      const sourceTarget = findContradictionSourceTarget(location);
      if (!sourceTarget) return false;
      navigateToResolvedTarget({ qid: null, element: sourceTarget });
      setPendingContradictionNavigation(null);
      return true;
    };

    if (tryResolve()) return;

    const intervalId = window.setInterval(() => {
      if (tryResolve()) {
        window.clearInterval(intervalId);
        window.clearTimeout(timeoutId);
      }
    }, 50);
    const timeoutId = window.setTimeout(() => {
      window.clearInterval(intervalId);
      setPendingContradictionNavigation(null);
    }, 3000);

    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [
    pendingContradictionNavigation,
    currentCategory,
    navigateToResolvedTarget,
  ]);

  const saveFormData = async (data: OASISFormData) => {
    const saveToken = beginSave();
    try {
      const dataToSave = schema ? getOASISFormData(data, schema) : data;
      await onSave(dataToSave);
      setLastSaved(new Date());
      resolveSave(saveToken, 'saved');
    } catch (error) {
      if (error instanceof SaveDeclinedError) {
        // The user declined the signature-invalidation dialog. The edits
        // are still unsaved (the parent discards them by remounting this
        // form), but nothing failed, so don't report an error.
        resolveSave(saveToken, 'declined');
        return;
      }
      console.error('Auto-save failed:', error);
      resolveSave(saveToken, 'error');
    }
  };

  // Debounced save function (save after 2 seconds of inactivity, or immediately if there are signatures)
  const debouncedSave = useDebounce((data: OASISFormData) => {
    void saveFormData(data);
  }, getOasisAutosaveDebounceMs(hasActiveSignatures));

  useEffect(() => {
    onSaveStatusChange?.(saveStatus, changeToken);
  }, [saveStatus, changeToken, onSaveStatusChange]);

  const runInlineValidation = useCallback(
    async (data: OASISFormData) => {
      if (!showInlineValidation || !visitId || !schema) return;

      setInlineValidationLoading(true);
      setInlineValidationErrorMessage(null);

      try {
        const response = await validateOasisVisitsVisitIdValidateOasisPost({
          path: { visit_id: visitId },
          body: {
            answers: getOASISFormData(data, schema) as Record<string, unknown>,
          },
        });

        if (response.error) {
          setInlineValidationErrorMessage(
            getUserFacingApiMessage(
              response,
              'Validation failed. Please try again.'
            )
          );
          setInlineValidationErrors([]);
          setInlineContradictions([]);
        } else {
          setInlineValidationErrors(response.data?.errors || []);
          setInlineContradictions(response.data?.contradictions || []);
        }
      } catch (err) {
        console.error('Inline validation failed:', err);
        setInlineValidationErrorMessage('Validation failed. Please try again.');
        setInlineValidationErrors([]);
        setInlineContradictions([]);
      } finally {
        setInlineValidationLoading(false);
        setHasValidatedOnce(true);
      }
    },
    [schema, showInlineValidation, visitId]
  );

  const debouncedInlineValidation = useDebounce((data: OASISFormData) => {
    void runInlineValidation(data);
  }, 900);

  const scrubberExpandedBodyRef = useRef<HTMLDivElement>(null);
  const scrubberStableBodyHeightRef = useRef(0);

  useLayoutEffect(() => {
    if (!showInlineValidation || !isOasis) return;
    if (!isScrubberExpanded) {
      scrubberStableBodyHeightRef.current = 0;
      return;
    }
    if (inlineValidationLoading) return;
    const el = scrubberExpandedBodyRef.current;
    if (!el) return;
    scrubberStableBodyHeightRef.current = el.offsetHeight;
  }, [
    showInlineValidation,
    isOasis,
    isScrubberExpanded,
    inlineValidationLoading,
    inlineValidationErrors,
    inlineValidationErrorMessage,
    hasValidatedOnce,
    scrubberTab,
    currentCategory,
  ]);

  useEffect(() => {
    setCurrentCategory(null);
  }, [visitType, setCurrentCategory]);

  useEffect(() => {
    const fetchSchema = async () => {
      setIsLoading(true);
      setError(null);

      try {
        let schemaData: OASISSchema;
        // Captured from the versioned response so `categoryList` below can
        // honor `section_order` without round-tripping through Zustand
        // (state setters are async, the local `schemaVersion` closed over
        // at render time is stale here).
        let fetchedVersion: FormSchemaVersion | null = null;

        if (useVersionedFormSchemas) {
          const response = await getVisitSchemaRogerdocV2VisitsVisitIdSchemaGet(
            {
              path: { visit_id: visitId },
            }
          );

          if (!response?.data) {
            throw new Error(
              'Could not load the required form schema. Please contact support.'
            );
          }

          const { version, schema_data } = response.data as {
            version: FormSchemaVersion;
            schema_data: OASISSchema;
          };

          schemaData = schema_data;
          fetchedVersion = version;
          setSchemaVersion(version);

          const bespokePin = pinFromFormSchemaVersion(
            version.form_type as RogerDocFormType,
            version
          );
          if (!bespokePin) {
            throw new Error(
              'The visit schema is missing form type or version metadata required to load customized section layouts. Please contact support.'
            );
          }

          const bespokeRes =
            await listEnabledSectionsBespokeTemplatesEnabledGet({
              query: {
                form_type: bespokePin.form_type,
                schema_version_name: bespokePin.schema_version_name,
                form_schema_version: bespokePin.form_schema_version,
              },
            });
          const entries =
            (bespokeRes?.data as EnabledBespokeEntry[] | undefined) ?? [];
          const sectionMap = new Map(
            entries.map(e => [
              e.section_name,
              {
                section_name: e.section_name,
                layout_json:
                  (e.layout_json as
                    | import('./bespoke/layout/types').LayoutNode[]
                    | null) ?? null,
              },
            ])
          );
          setBespokeDbConfig(() => new Map([[version.name, sectionMap]]));
        } else {
          const response = await getQuestionSchemaRogerdocQuestionSchemaGet({
            query: { visit_type: visitType },
          });

          if (!response?.data) {
            throw new Error('Empty schema response');
          }

          schemaData = response.data as OASISSchema;
        }

        setSchema(schemaData);

        // Prefer the FormSchemaVersion's `section_order` for both the section list
        // and its order (ROG-2295).
        //
        //   1. Filter `section_order` to keys that actually exist in
        //      `schemaData` — an FSV that lists a section the build response
        //      doesn't populate must not produce an empty tab.
        //   2. Append any `schemaData` keys the FormSchemaVersion's `section_order`
        //      omits — in case we have typos or missed a section in section_order.
        const categoryList = (fetchedVersion?.section_order ?? [])
          .map(item => item.section_id)
          .filter(sectionId => sectionId in schemaData);
        for (const key of Object.keys(schemaData)) {
          if (!categoryList.includes(key)) categoryList.push(key);
        }
        if (supportsActiveDiagnosisPage(visitType)) {
          categoryList.push(ACTIVE_DIAGNOSIS_CATEGORY);
        }
        // A visit gets a Goals page when it can create goals or record progress
        // against them. Transfer and death notes can do neither.
        if (isOasis && showsGoalsSection(visitType)) {
          categoryList.push(GOALS_CATEGORY);
        }
        setCategories(categoryList);
        if (categoryList.length > 0) {
          setCurrentCategory(categoryList[0]);
        }
      } catch (err) {
        console.error('Failed to fetch question schema:', err);
        setError('Failed to load form schema. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchSchema();
  }, [
    visitId,
    visitType,
    useVersionedFormSchemas,
    setSchema,
    setIsLoading,
    setError,
    setCurrentCategory,
    setSchemaVersion,
    setCategories,
    setBespokeDbConfig,
    isOasis,
  ]);

  useEffect(() => {
    hydratedVisitIdRef.current = null;
    hasRunInitialValidation.current = false;
    // Seed answers immediately on visit navigation so the UI is not blank while
    // schema/bespoke layouts load. `initialData` is intentionally omitted from
    // deps — this runs once per visitId (OASISForm remounts via key= anyway).
    setFormData(initialData ?? {});
  }, [visitId, setFormData]);

  useEffect(() => {
    if (!schema || isLoading) return;

    // Versioned visits load bespoke layouts in a separate React state update
    // from schema (Zustand). Wait until fetchSchema has populated bespokeDbConfig.
    if (
      useVersionedFormSchemas &&
      schemaVersion?.name &&
      bespokeDbConfig.size === 0
    ) {
      return;
    }

    if (hydratedVisitIdRef.current !== visitId) {
      hydratedVisitIdRef.current = visitId;

      setFormData(initialData || {});

      if (showInlineValidation) {
        hasRunInitialValidation.current = true;
        void runInlineValidation(initialData || {});
      }
      return;
    }

    if (showInlineValidation && !hasRunInitialValidation.current) {
      hasRunInitialValidation.current = true;
      void runInlineValidation(useOASISFormStore.getState().formData);
    }
    /* `initialData` is omitted on purpose: autosave updates `visit.visit_data` while the user may have newer edits; re-hydrating would flash stale values. */
  }, [
    visitId,
    schema,
    isLoading,
    bespokeDbConfig,
    useVersionedFormSchemas,
    schemaVersion?.name,
    setFormData,
    showInlineValidation,
    runInlineValidation,
  ]);

  const handleCategoryChange = (category: string) => {
    setCurrentCategory(category);
  };

  const handleFormDataChange = (updates: Partial<OASISFormData>) => {
    const newFormData: OASISFormData = { ...formData };

    Object.entries(updates).forEach(([key, value]) => {
      if (value === undefined) {
        delete newFormData[key];
      } else {
        newFormData[key] = value;
      }
    });

    setFormData(newFormData);
    reportEdit();
    debouncedSave(newFormData);
    if (showInlineValidation) {
      debouncedInlineValidation(newFormData);
    }
  };

  // Build each tab's lists once; panel picks the active tab without refiltering.
  const scrubberListModel = useMemo(() => {
    if (!showInlineValidation || !isOasis) return null;

    const totalFatalCount = inlineValidationErrors.filter(
      err => err.severity === 'fatal'
    ).length;
    const totalWarningCount = inlineValidationErrors.filter(
      err => err.severity === 'warning'
    ).length;

    const fatalsByTab = {} as Record<ScrubberTab, OasisValidationError[]>;
    const advisoryByTab = {} as Record<ScrubberTab, ScrubberListItem[]>;
    const tabCounts = {} as Record<ScrubberTab, number>;
    for (const tab of SCRUBBER_TABS) {
      fatalsByTab[tab] = buildFatalScrubberErrors(
        inlineValidationErrors,
        tab,
        currentCategory,
        qidToCategoryMap
      );
      advisoryByTab[tab] = buildAdvisoryScrubberItems(
        inlineValidationErrors,
        inlineContradictions,
        tab,
        currentCategory,
        qidToCategoryMap
      );
      tabCounts[tab] = fatalsByTab[tab].length + advisoryByTab[tab].length;
    }

    return {
      totalFatalCount,
      totalWarningCount,
      fatalsByTab,
      advisoryByTab,
      tabCounts,
    };
  }, [
    showInlineValidation,
    isOasis,
    inlineValidationErrors,
    inlineContradictions,
    currentCategory,
    qidToCategoryMap,
  ]);

  const renderInlineValidationPanel = () => {
    if (!scrubberListModel) return null;

    const {
      totalFatalCount,
      totalWarningCount,
      fatalsByTab,
      advisoryByTab,
      tabCounts,
    } = scrubberListModel;
    const visibleFatals = fatalsByTab[scrubberTab];
    const visibleAdvisory = advisoryByTab[scrubberTab];

    const visibleTabs = SCRUBBER_TAB_DEFINITIONS.filter(
      definition => definition.id !== 'qa' || qaTabEnabled
    );
    const scrubberNoIssuesMessage =
      visibleTabs.find(definition => definition.id === scrubberTab)
        ?.emptyMessage ?? 'No issues';

    const hasVisibleBodyItems =
      visibleFatals.length > 0 || visibleAdvisory.length > 0;

    return (
      <div className='mb-3 rounded-md border bg-muted/20 p-3'>
        {/* Toggle is separate from section-filter buttons so keyboard events
            on Section/Misc/All do not also collapse the scrubber. */}
        <div className='w-full flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5'>
          <button
            type='button'
            onClick={() => setIsScrubberExpanded(prev => !prev)}
            className='flex items-center gap-2 text-sm min-w-0 text-left cursor-pointer'
            aria-expanded={isScrubberExpanded}
            aria-label='Toggle Live Scrubber'
          >
            <AlertTriangle className='h-4 w-4 text-amber-600' />
            <span className='font-medium'>Live Scrubber</span>
            {inlineValidationLoading ? (
              <span className='text-xs text-muted-foreground'>Checking...</span>
            ) : inlineValidationErrorMessage ? (
              <span className='text-xs text-destructive'>
                Validation unavailable
              </span>
            ) : (
              <span className='text-xs text-muted-foreground'>
                Checks run automatically
              </span>
            )}
            {isScrubberExpanded ? (
              <ChevronUp className='h-4 w-4 text-muted-foreground' />
            ) : (
              <ChevronDown className='h-4 w-4 text-muted-foreground' />
            )}
          </button>
          <div className='flex flex-wrap items-center gap-2'>
            {/* Stays put once validation has run, even at zero issues — the
                empty-state copy is tab-scoped, so hiding the pills would leave
                no way to tell which tab it is describing (EMR-809 review). */}
            {hasValidatedOnce && (
              <div className='flex flex-wrap gap-0.5'>
                {visibleTabs.map(({ id: tab, label }) => {
                  const count = tabCounts[tab];
                  return (
                    <button
                      key={tab}
                      type='button'
                      onClick={() => {
                        setScrubberTab(tab);
                        if (!isScrubberExpanded) setIsScrubberExpanded(true);
                      }}
                      className={`px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors ${
                        scrubberTab === tab
                          ? 'bg-foreground/10 text-foreground'
                          : 'text-muted-foreground hover:text-foreground/70'
                      }`}
                    >
                      {label ??
                        resolveCategoryLabel(
                          currentCategory || '',
                          labelByCategory
                        )}
                      {count > 0 && (
                        <span className='ml-0.5 opacity-70'>({count})</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
            {(totalFatalCount > 0 || totalWarningCount > 0) && (
              <div className='flex items-center gap-2 text-xs'>
                {totalFatalCount > 0 && (
                  <span className='rounded bg-destructive/10 px-2 py-0.5 text-destructive'>
                    {totalFatalCount} fatal
                  </span>
                )}
                {totalWarningCount > 0 && (
                  <span className='rounded bg-amber-100 px-2 py-0.5 text-amber-700'>
                    {totalWarningCount} warning
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        {isScrubberExpanded && (
          <div
            ref={scrubberExpandedBodyRef}
            className='mt-2 max-h-56 overflow-y-auto'
            style={
              inlineValidationLoading && scrubberStableBodyHeightRef.current > 0
                ? { minHeight: scrubberStableBodyHeightRef.current }
                : undefined
            }
          >
            {inlineValidationLoading ? (
              <div className='flex items-center gap-2 text-xs text-muted-foreground'>
                <Loader2 className='h-3.5 w-3.5 shrink-0 animate-spin' />
                Running live checks...
              </div>
            ) : inlineValidationErrorMessage ? (
              <p className='text-xs text-destructive'>
                {inlineValidationErrorMessage}
              </p>
            ) : hasVisibleBodyItems ? (
              <div className='space-y-2'>
                {visibleFatals.map((err, idx) => (
                  <CmsIssueCard
                    key={`fatal-${err.edit_id}-${idx}`}
                    error={err}
                    qidToQuestionText={qidToQuestionText}
                    onJump={navigateToError}
                  />
                ))}
                {visibleAdvisory.map((item, idx) =>
                  item.kind === 'cms' ? (
                    <CmsIssueCard
                      key={`warning-${item.error.edit_id}-${idx}`}
                      error={item.error}
                      qidToQuestionText={qidToQuestionText}
                      onJump={navigateToError}
                    />
                  ) : (
                    <ContradictionFindingCard
                      key={item.finding.rule_id}
                      finding={item.finding}
                      currentCategory={currentCategory}
                      qidToCategoryMap={qidToCategoryMap}
                      labelByCategory={labelByCategory}
                      onJump={navigateToContradiction}
                      onHighlightLocation={navigateToContradictionLocation}
                    />
                  )
                )}
              </div>
            ) : hasValidatedOnce ? (
              <div className='text-xs text-muted-foreground'>
                {scrubberNoIssuesMessage}
              </div>
            ) : (
              <div className='text-xs text-muted-foreground'>
                Running checks for loaded data.
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const currentQuestions = useMemo(
    () => (currentCategory && schema ? schema[currentCategory] : []) || [],
    [currentCategory, schema]
  );

  const schemaQuestionOptions: SchemaQuestionOption[] = useMemo(() => {
    if (!schema) return [];
    const options: SchemaQuestionOption[] = [];
    const seen = new Set<string>();

    const collect = (questions: Question[], category: string) => {
      for (const q of questions) {
        if (!seen.has(q.qid)) {
          seen.add(q.qid);
          options.push({
            qid: q.qid,
            label: q.question || q.qid,
            category,
          });
        }
        if (q.subitems) {
          for (const children of Object.values(q.subitems)) {
            collect(children, category);
          }
        }
      }
    };

    for (const [category, questions] of Object.entries(schema)) {
      collect(questions, category);
    }
    return options;
  }, [schema]);

  const gridItems = useMemo(
    () => flattenQuestions(currentQuestions),
    [currentQuestions]
  );

  // Minimal visit shape for the embedded goals section. Built from primitives so
  // the memoized form does not take the whole (frequently-replaced) visit object
  // as a prop.
  const goalsVisit: GoalsTabVisit = useMemo(
    () => ({
      id: visitId,
      status: visitStatus,
      visit_type: visitType,
      episode_start_date: episodeStartDate,
      episode_end_date: episodeEndDate,
    }),
    [visitId, visitStatus, visitType, episodeStartDate, episodeEndDate]
  );

  // Patient names are only shown in GoalsTab's dialog header, which inline mode
  // hides, so empty strings are safe here.
  const renderGoalsSection = () => (
    <GoalsTab
      open
      onClose={noop}
      patientFirstName=''
      patientLastName=''
      visit={goalsVisit}
      inline
    />
  );

  // PatientMedications requires a handler; forward to the parent when provided.
  const handleMedicationOrderCreated = useCallback(
    (order: OrderResponse) => onMedicationOrderCreated?.(order),
    [onMedicationOrderCreated]
  );

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-12'>
        <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
      </div>
    );
  }

  if (error) {
    return <p className='text-destructive text-sm py-4'>{error}</p>;
  }

  if (!schema || categories.length === 0) {
    return (
      <p className='text-muted-foreground text-sm py-4'>
        No form schema available for this visit type.
      </p>
    );
  }

  const renderGridItem = (item: GridItem) => {
    const spanClass = SPAN_CLASSES[item.span];

    return (
      <div key={item.question.qid} className={`${spanClass} min-w-0`}>
        <OASISQuestionRenderer
          question={item.question}
          value={formData[item.question.qid]}
          onChange={value => {
            const updates: Partial<OASISFormData> = {
              [item.question.qid]: value,
            };

            const clearUpdates = getSubitemClearUpdates(
              item.question,
              value,
              formData
            );
            Object.assign(updates, clearUpdates);

            handleFormDataChange(updates);
          }}
          formData={formData}
          containerSpan={item.span}
          onFormDataChange={handleFormDataChange}
          highlightedQid={highlightedQid}
        />
      </div>
    );
  };

  // Wounds and meds are surfaced at the bottom of their matching OASIS sections
  // so clinicians can review them before submitting the chart.
  const renderEmbeddedSectionModules = () => {
    const category = currentCategory || '';

    // The OASIS schema carries a Skin section for every discipline, but only the
    // visit types WellSky gives a wound section get the Wounds module.
    if (category === SKIN_SECTION_ID && supportsWounds(visitType)) {
      return (
        <div className='mt-6 pt-6 border-t border-border'>
          <h4 className='text-sm font-semibold text-foreground mb-3'>Wounds</h4>
          <PatientWounds
            patientId={patientId}
            visitId={visitId}
            visitStatus={visitStatus}
          />
        </div>
      );
    }

    if (category === MEDICATIONS_SECTION_ID) {
      return (
        <>
          <div className='mt-6 pt-6 border-t border-border'>
            <h4 className='text-sm font-semibold text-foreground mb-3'>
              Medications
            </h4>
            <PatientMedications
              patientId={patientId}
              episodeId={episodeId}
              visitId={visitId}
              visitStatus={visitStatus}
              onMedicationOrderCreated={handleMedicationOrderCreated}
            />
          </div>
          {patient && (
            <div className='mt-6 pt-6 border-t border-border'>
              <h4 className='text-sm font-semibold text-foreground mb-3'>
                Allergies
              </h4>
              <PatientAllergies patient={patient} />
            </div>
          )}
        </>
      );
    }

    return null;
  };

  const renderOasisSectionContent = () => {
    const BespokeComponent = resolveBespokeSection(
      bespokeDbConfig,
      schemaVersion?.name,
      currentCategory || ''
    );

    const standardGrid = (
      <div className='grid grid-cols-12 gap-3'>
        {gridItems.map(item => renderGridItem(item))}
      </div>
    );

    if (BespokeComponent && schemaVersion) {
      return (
        <>
          <div className='mb-4 pb-2 border-b border-border flex items-center justify-between'>
            <h3 className='text-base font-bold text-foreground'>
              {resolveCategoryLabel(currentCategory || '', labelByCategory)}
            </h3>
          </div>
          {renderInlineValidationPanel()}
          <BespokeComponent
            answers={formData}
            onAnswerChange={(qid, value) =>
              handleFormDataChange({ [qid]: value })
            }
            onFormDataChange={handleFormDataChange}
            schema={currentQuestions}
            schemaQuestionOptions={schemaQuestionOptions}
            sectionName={currentCategory || ''}
            fallback={standardGrid}
          />
          {renderEmbeddedSectionModules()}
        </>
      );
    }

    return (
      <>
        <div className='mb-4 pb-2 border-b border-border flex items-center justify-between'>
          <h3 className='text-base font-bold text-foreground'>
            {resolveCategoryLabel(currentCategory || '', labelByCategory)}
          </h3>
        </div>
        {renderInlineValidationPanel()}

        {standardGrid}
        {renderEmbeddedSectionModules()}
      </>
    );
  };

  // ─── Non-OASIS: single merged schema bucket (`All`); section chrome → layout_json ───
  if (!isOasis) {
    const wholeSectionKey = categories[0] ?? NON_OASIS_WHOLE_VISIT_SECTION_NAME;
    const categoryQuestions = schema[wholeSectionKey] || [];
    const items = flattenQuestions(categoryQuestions);
    const BespokeComponent = schemaVersion
      ? resolveBespokeSection(
          bespokeDbConfig,
          schemaVersion.name,
          wholeSectionKey
        )
      : null;

    const standardGrid = (
      <div className='grid grid-cols-12 gap-3'>
        {items.map(item => renderGridItem(item))}
      </div>
    );

    return (
      <div className='min-w-0'>
        {renderInlineValidationPanel()}
        {BespokeComponent ? (
          <BespokeComponent
            answers={formData}
            onAnswerChange={(qid, value) =>
              handleFormDataChange({ [qid]: value })
            }
            onFormDataChange={handleFormDataChange}
            schema={categoryQuestions}
            schemaQuestionOptions={schemaQuestionOptions}
            sectionName={wholeSectionKey}
            fallback={standardGrid}
          />
        ) : (
          standardGrid
        )}
        {/* Routine visits are a single page, so the medications, allergies,
            wounds, and goals modules are surfaced inline at the bottom of the
            form, matching the OASIS section embeds. This lets clinicians review
            and edit them before submitting the chart instead of leaving the
            form to reach separate tabs. Each module self-gates editing by visit
            status, so it stays read-only where it is not eligible.

            Medications and allergies are scoped to skilled-nursing (RN/SN)
            routine notes: the non-OASIS branch also renders PT/OT/ST/MSW/HHA
            notes, and those clinical modules do not belong on those disciplines'
            forms. Wounds and goals use their own per-visit-type lists instead,
            since WellSky puts a wound section on PT/OT notes but not on ST,
            supervisory, MSW, or aide notes. */}
        {isSNDiscipline(visitType) && (
          <>
            <div className='mt-6 pt-6 border-t border-border'>
              <h3 className='text-base font-bold text-foreground mb-3'>
                Medications
              </h3>
              <PatientMedications
                patientId={patientId}
                episodeId={episodeId}
                visitId={visitId}
                visitStatus={visitStatus}
                onMedicationOrderCreated={handleMedicationOrderCreated}
              />
            </div>
            {patient && (
              <div className='mt-6 pt-6 border-t border-border'>
                <h3 className='text-base font-bold text-foreground mb-3'>
                  Allergies
                </h3>
                <PatientAllergies patient={patient} />
              </div>
            )}
          </>
        )}
        {supportsWounds(visitType) && (
          <div className='mt-6 pt-6 border-t border-border'>
            <h3 className='text-base font-bold text-foreground mb-3'>Wounds</h3>
            <PatientWounds
              patientId={patientId}
              visitId={visitId}
              visitStatus={visitStatus}
            />
          </div>
        )}
        {showsGoalsSection(visitType) && (
          <div className='mt-6 pt-6 border-t border-border'>
            <h3 className='text-base font-bold text-foreground mb-3'>
              Goals &amp; Interventions
            </h3>
            {renderGoalsSection()}
          </div>
        )}
      </div>
    );
  }
  return (
    <div className='flex flex-col md:flex-row'>
      {/* Mobile: collapsible section picker */}
      <div className='md:hidden mb-4'>
        <button
          type='button'
          onClick={() => setIsMobileNavOpen(open => !open)}
          aria-expanded={isMobileNavOpen}
          aria-controls='oasis-mobile-section-list'
          className='w-full flex items-center justify-between gap-2 rounded-md border border-border bg-background px-3 py-2.5 text-sm font-medium text-left'
        >
          <span className='truncate'>
            {resolveCategoryLabel(currentCategory || '', labelByCategory)}
          </span>
          {isMobileNavOpen ? (
            <ChevronUp className='h-4 w-4 shrink-0 text-muted-foreground' />
          ) : (
            <ChevronDown className='h-4 w-4 shrink-0 text-muted-foreground' />
          )}
        </button>
        {isMobileNavOpen && (
          <div
            id='oasis-mobile-section-list'
            className='mt-2 rounded-md border border-border p-2 max-h-80 overflow-y-auto'
          >
            <OASISNavigation
              categories={categories}
              currentCategory={currentCategory}
              onCategoryChange={category => {
                setIsMobileNavOpen(false);
                handleCategoryChange(category);
              }}
              schema={schema}
              formData={formData}
              fatalCountByCategory={fatalCountByCategory}
              advisoryCountByCategory={advisoryCountByCategory}
              labelByCategory={labelByCategory}
            />
          </div>
        )}
      </div>

      {/* Sidebar navigation (desktop) */}
      <div className='hidden md:block w-48 flex-shrink-0 pr-4 border-r border-border'>
        <div className='sticky top-0'>
          <div className='flex items-center justify-between mb-2 px-2.5'>
            <span className='text-[10px] font-semibold text-muted-foreground uppercase tracking-wider'>
              Sections
            </span>
          </div>
          <OASISNavigation
            categories={categories}
            currentCategory={currentCategory}
            onCategoryChange={handleCategoryChange}
            schema={schema}
            formData={formData}
            fatalCountByCategory={fatalCountByCategory}
            advisoryCountByCategory={advisoryCountByCategory}
            labelByCategory={labelByCategory}
          />
        </div>
      </div>

      {/* Form content */}
      <div className='flex-1 min-w-0 md:pl-5'>
        {currentCategory === ACTIVE_DIAGNOSIS_CATEGORY ? (
          <>
            <div className='mb-4 pb-2 border-b border-border'>
              <h3 className='text-base font-bold text-foreground'>
                Active Diagnosis
              </h3>
            </div>
            {/* Same Live Scrubber chrome as OASIS sections — including CMS
                fatal mode, even though Active Diagnosis has no CMS qids. */}
            {renderInlineValidationPanel()}
            <VisitActiveDiagnosis visitId={visitId} />
          </>
        ) : currentCategory === GOALS_CATEGORY ? (
          <>
            <div className='mb-4 pb-2 border-b border-border'>
              <h3 className='text-base font-bold text-foreground'>
                Goals &amp; Interventions
              </h3>
            </div>
            {renderGoalsSection()}
          </>
        ) : (
          renderOasisSectionContent()
        )}
      </div>
    </div>
  );
};

/**
 * Memoized so the parent's per-keystroke status state updates do not
 * re-render the whole question tree; keep the function props stable.
 */
export const OASISForm = React.memo(OASISFormComponent);
OASISForm.displayName = 'OASISForm';
